import {inject, Injectable} from '@angular/core';
import {VaultsWithLinksService} from '../../api/services/vaults-with-links.service';
import {PermissionStore} from '../../stores/permission.store';
import {SnackbarService} from '../snackbar/snackbar.service';
import {UserGroupsWithLinksService} from '../../api/services/user-groups-with-links.service';
import {DocumentsWithLinksService} from '../../api/services/documents-with-links.service';
import {CheckedOutDocumentsWithLinksService} from '../../api/services/checked-out-documents-with-links.service';
import {TrashedDocumentsWithLinksService} from '../../api/services/trashed-documents-with-links.service';
import {DocumentTypeCategoriesWithLinksService} from '../../api/services/document-type-categories-with-links.service';
import {PermissionDirectiveSettings} from '../../models/permission-settings.model';
import {PermissionOperator} from '../../types/permissions/permission-operator.type';
import {PermissionSettings} from '../../types/permissions/permission-settings.type';
import {DocumentPermission} from '../../types/permissions/document-permission.type';
import {DocumentWithLinks} from '../../api/models/document-with-links';
import {CheckedOutDocumentPermission} from '../../types/permissions/checked-out-document-permission.type';
import {firstValueFrom, lastValueFrom} from 'rxjs';
import {AuthService} from '../auth/auth.service';
import {UserGroupPermission} from '../../types/permissions/user-group-permission.type';
import {VaultPermission} from '../../types/permissions/vault-permission.type';
import {DocumentTypeCategoryPermission} from '../../types/permissions/document-type-category-permission.type';
import {TrashedDocumentsPermission} from '../../types/permissions/trashed-documents-permission.type';

@Injectable({
    providedIn: 'root'
})
export class PermissionService {
    private readonly authService = inject(AuthService);
    private readonly snackbarService = inject(SnackbarService);
    private readonly permissionStore = inject(PermissionStore);
    private readonly userGroupsWithLinksService = inject(UserGroupsWithLinksService);
    private readonly vaultsWithLinksService = inject(VaultsWithLinksService);
    private readonly documentsWithLinksService = inject(DocumentsWithLinksService);
    private readonly checkedOutDocumentsWithLinksService = inject(CheckedOutDocumentsWithLinksService);
    private readonly trashedDocumentsWithLinksService = inject(TrashedDocumentsWithLinksService);
    private readonly documentTypeCategoriesWithLinksService = inject(DocumentTypeCategoriesWithLinksService);
    private readonly loadingQueue: Map<string, Promise<void>>;

    public constructor() {
        this.loadingQueue = new Map<string, Promise<void>>();
    }

    public async fetchUserGroupPermissions(userGroupId: string): Promise<void> {
        const currentPermissions = this.permissionStore.getValue().userGroupPermissions;
        if (userGroupId in currentPermissions) {
            return;
        }

        const bearer = await this.authService.getBearer();
        try {
            const userGroupWithLinks = await firstValueFrom(this.userGroupsWithLinksService.UserGroupsWithLinksGet({
                userGroupId,
                Authorization: bearer
            }));
            const userGroupPermissions = structuredClone(currentPermissions);
            userGroupPermissions[userGroupId] = userGroupWithLinks.links.map(link => link.rel) as Array<UserGroupPermission>;
            this.permissionStore.update({ userGroupPermissions });
        } catch (error) {
            console.error(error);
            this.snackbarService.showError('ERROR.PERMISSION.USER_GROUP_WITH_LINK_FETCHING', error as Error);
        }
    }

    public async fetchVaultPermissions(vaultId: string, refresh: boolean = false): Promise<void> {
        const currentPermissions = this.permissionStore.getValue().vaultPermissions;
        if (!refresh && vaultId in currentPermissions) {
            return;
        }

        const bearer = await this.authService.getBearer();
        try {
            const vaultWithLinks = await firstValueFrom(this.vaultsWithLinksService.VaultsWithLinksGet({
                vaultId,
                Authorization: bearer
            }));
            const vaultPermissions = structuredClone(currentPermissions);
            vaultPermissions[vaultId] = vaultWithLinks.links.map(link => link.rel) as Array<VaultPermission>;
            this.permissionStore.update({ vaultPermissions });
        } catch (error) {
            console.error(error);
            this.snackbarService.showError('ERROR.PERMISSION.VAULT_WITH_LINK_FETCHING', error as Error);
        }
    }

    public async fetchDocumentPermission(documentId: string, forceFetch: boolean = false): Promise<void> {
        if (this.loadingQueue.has(documentId)) {
            await this.loadingQueue.get(documentId);
        }

        const currentPermissions = this.permissionStore.getValue().documentPermissions;
        if (documentId in currentPermissions && !forceFetch) {
            return;
        }

        const loadDocumentPermissionsPromise = (async (): Promise<void> => {
            const newDocumentPermissions: Record<string, Array<DocumentPermission>> = structuredClone(currentPermissions);
            const bearer = await this.authService.getBearer();
            try {
                const documentsWithLinks: DocumentWithLinks = await firstValueFrom(this.documentsWithLinksService.DocumentsWithLinksGet({
                    documentId,
                    Authorization: bearer
                }));

                    newDocumentPermissions[documentId] = documentsWithLinks.links.map(link => link.rel as DocumentPermission);
                } catch (error) {
                    delete newDocumentPermissions[documentId];
                    this.snackbarService.showError('ERROR.PERMISSION.DOCUMENT_WITH_LINK_FETCHING', error as Error);
                } finally {
                    this.loadingQueue.delete(documentId);
                }

            this.permissionStore.update({ documentPermissions: structuredClone(newDocumentPermissions) });
        })();

        this.loadingQueue.set(documentId, loadDocumentPermissionsPromise);
        await loadDocumentPermissionsPromise;
    }

    public async fetchCheckedOutDocumentPermission(checkedOutDocumentId: string): Promise<void> {
        const currentPermissions = this.permissionStore.getValue().checkedOutDocumentPermissions;
        if (checkedOutDocumentId in currentPermissions) {
            return;
        }

        const bearer = await this.authService.getBearer();
        try {
            const checkedOutDocumentsWithLinks = await firstValueFrom(this.checkedOutDocumentsWithLinksService.CheckedOutDocumentsWithLinksGet({
                documentId: checkedOutDocumentId,
                Authorization: bearer
            }));
            const checkedOutDocumentPermissions: Record<string, Array<CheckedOutDocumentPermission>> = structuredClone(currentPermissions);
            checkedOutDocumentPermissions[checkedOutDocumentId] = checkedOutDocumentsWithLinks.links.map(link => link.rel as CheckedOutDocumentPermission);
            this.permissionStore.update({ checkedOutDocumentPermissions });
        } catch (error) {
            console.error(error);
            this.snackbarService.showError('ERROR.PERMISSION.DOCUMENT_WITH_LINK_FETCHING', error as Error);
        }
    }

    public async fetchTrashedDocumentPermission(documentId: string): Promise<void> {
        const currentPermissions = this.permissionStore.getValue().trashedDocumentPermissions;
        if (documentId in currentPermissions) {
            return;
        }

        const bearer = await this.authService.getBearer();
        try {
            const trashedDocumentsWithLinks = await lastValueFrom(this.trashedDocumentsWithLinksService.TrashedDocumentsWithLinksGet({
                documentId,
                Authorization: bearer
            }));
            const trashedDocumentPermissions = structuredClone(currentPermissions);
            trashedDocumentPermissions[documentId] = trashedDocumentsWithLinks.links.map(link => link.rel) as Array<TrashedDocumentsPermission>;
            this.permissionStore.update({ trashedDocumentPermissions });
        } catch (error) {
            console.error(error);
            this.snackbarService.showError('ERROR.PERMISSION.DOCUMENT_WITH_LINK_FETCHING', error as Error);
        }
    }

    public clearPermissions(): void {
        this.permissionStore.reset();
    }

    public removeDocumentPermissionCacheById(documentId: string): void {
        const documentPermissions = structuredClone(this.permissionStore.getValue().documentPermissions);
        if (documentId in documentPermissions) {
            delete documentPermissions[documentId];
        }
        this.permissionStore.update({ documentPermissions });
    }

    public extractPermissions(permissions: PermissionSettings | PermissionDirectiveSettings | Array<PermissionSettings>): Array<PermissionSettings> {
        let permissionSettings: Array<PermissionSettings>;
        if ('operator' in permissions) {
            permissionSettings = permissions.permissions;
        } else {
            permissionSettings = Array.isArray(permissions) ? permissions : [permissions];
        }
        return permissionSettings;
    }

    public extractPermissionOperator(appPermission: PermissionSettings | Array<PermissionSettings> | PermissionDirectiveSettings): PermissionOperator {
        let operator = 'AND' as PermissionOperator;
        if ('operator' in appPermission) {
            operator = appPermission.operator;
        }
        return operator;
    }

    public async fetchDocumentCategoryPermission(documentTypeCategoryId: string): Promise<void> {
        const currentPermissions = this.permissionStore.getValue().documentTypeCategoryPermissions;
        if (documentTypeCategoryId in currentPermissions) {
            return;
        }

        const bearer = await this.authService.getBearer();
        try {
            const documentTypeCategoriesWithLinks = await firstValueFrom(this.documentTypeCategoriesWithLinksService.DocumentTypeCategoriesWithLinksGet({
                documentTypeCategoryId,
                Authorization: bearer
            }));
            const documentTypeCategoryPermissions = structuredClone(currentPermissions);
            documentTypeCategoryPermissions[documentTypeCategoryId] = documentTypeCategoriesWithLinks.links.map(link => link.rel) as Array<DocumentTypeCategoryPermission>;
            this.permissionStore.update({ documentTypeCategoryPermissions });
        } catch (error) {
            console.error(error);
            this.snackbarService.showError('ERROR.PERMISSION.DOCUMENT_WITH_LINK_FETCHING', error as Error);
        }
    }
}
